// src/pages/Announcementnew2.js
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelectedCompany } from "../Commoncontext/SelectedCompanyContext";
import { useUsers } from "../Commoncontext/UsersContext";
import { useAnnouncements } from "../Commoncontext/AnnouncementsContext";
import { useToast } from "../Commoncontext/ToastContext";
import { fmtDateTime } from "../Utils/dates";
import useEditableEntity from "../Components/useEditableEntity";
import CommentsSection from "../Components/CommentsSection";
import AnnouncementDetailViewMode from "../Components/announcements/AnnouncementDetailViewMode";
import AnnouncementDetailEditMode from "../Components/announcements/AnnouncementDetailEditMode";
import IconAnnouncements from "../Images/IconAnnouncements";
import { canManageAnnouncements } from "../Utils/annoucementsPermissions";
import useResponsive from "../hooks/useResponsive";
import { VIEW_BREAKPOINTS } from "../Config/responsive";
import useAnnouncementComments from "../hooks/useAnnouncementComments";
import useAnnouncementAttachments from "../hooks/useAnnouncementAttachments";
import {normalizeAnnouncement, computeAnnouncementDirty } from "../Utils/AnnouncementsUtil";
import styles from "../Styles/Announcementnew2.module.css";

export default function Announcementnew2() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedCompanyId } = useSelectedCompany();
  const { user } = useUsers();
  const { showToast } = useToast();
  const { width } = useResponsive();
  const isCompactView = width <= VIEW_BREAKPOINTS.announcementsTableMin;
  const {
    active,
    detailLoading,
    fetchAnnouncementById,
    setActive,
    createAnnouncement,
    updateAnnouncement,
    addComment,
    likeComment,
    uploadAttachments,
    deleteAttachment,
    markViewed,
  } = useAnnouncements();
  const isNew = useMemo(() => !id, [id]);
  const isManager = useMemo(
    () => canManageAnnouncements(user, selectedCompanyId),
    [user, selectedCompanyId]
  );
  const emptyDraft = useMemo(
    () => ({
      id: null,
      title: "",
      content: "",
      category: "",
      audience: "residents_owners",
      attachments: [],
      createdAt: null,
      updatedAt: null,
      comments: [],
    }),
    []
  );
  const {
    draft,
    setField,
    setMany,
    saving,
    dirty,
    resetFromEntity,
    commitBase,
  } = useEditableEntity({
    initialDraft: emptyDraft,
    normalizeEntityToDraft: normalizeAnnouncement,
    computeDirty: computeAnnouncementDirty,
    buildPatch: (d) => ({
      title: (d.title || "").trim(),
      content: (d.content || "").trim(),
      category: d.category || "",
      audience: d.audience || "residents_owners",
    }),
    onSave: async (patch) => ({ savedDraft: patch }),
  });
  const safeDraft = useMemo(() => draft || emptyDraft, [draft, emptyDraft]);
  const announcementId = useMemo(() => id || active?.id || null, [id, active]);
  const isBusy = useMemo(
    () => Boolean(saving || detailLoading),
    [saving, detailLoading]
  );
  const [isEditing, setIsEditing] = useState(isNew);
  const draftRef = useRef(safeDraft);

  useEffect(() => {
    draftRef.current = safeDraft;
  }, [safeDraft]);

  const didForceFetchRef = useRef(new Set());
  const didMarkViewedRef = useRef(new Set());

  const {
    applyIncomingCommentsToDraft,
    handleSendComment,
    handleReplySubmit,
    handleLike,
  } = useAnnouncementComments({
    announcementId,
    selectedCompanyId,
    user,
    emptyDraft,
    draftRef,
    setMany,
    addComment,
    likeComment,
    showToast,
  });

  const { handleUploadFiles, handleDeleteAttachment } =
    useAnnouncementAttachments({
      announcementId,
      selectedCompanyId,
      emptyDraft,
      draftRef,
      setMany,
      uploadAttachments,
      deleteAttachment,
      showToast,
    });

  const initForCreate = useCallback(() => {
    const now = Date.now();
    const fresh = {
      ...emptyDraft,
      id: null,
      createdAt: now,
      updatedAt: now,
      attachments: [],
      comments: [],
    };
    resetFromEntity(null, { force: true });
    setActive?.(null);
    setMany(fresh);
    commitBase(fresh);
    setIsEditing(true);
  }, [emptyDraft, resetFromEntity, setActive, setMany, commitBase]);

  const initForView = useCallback(() => {
    resetFromEntity(null, { force: true });
    const canUseActive =
      active && id && String(active.id) === String(id) ? active : null;
    const baseEntity = canUseActive
      ? normalizeAnnouncement(canUseActive)
      : { ...emptyDraft, id };
    setMany(baseEntity);
    commitBase(baseEntity);
    setIsEditing(false);
  }, [id, active, emptyDraft, resetFromEntity, setMany, commitBase]);

  useEffect(() => {
    if (isNew) initForCreate();
    else initForView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, id]);

  const applyIncomingDetail = useCallback(
    (incomingEntity, { commitAsBase }) => {
      if (!incomingEntity) return;
      const incoming = normalizeAnnouncement(incomingEntity);
      const prev = draftRef.current || emptyDraft;
      const mergedComments = applyIncomingCommentsToDraft(incoming.comments);
      if (isEditing && dirty) {
        setMany({
          ...prev,
          updatedAt: incoming.updatedAt ?? prev.updatedAt,
          createdAt: incoming.createdAt ?? prev.createdAt,
          attachments: Array.isArray(incoming.attachments)
            ? incoming.attachments
            : prev.attachments || [],
          comments: mergedComments,
        });
        return;
      }
      const merged = {
        ...prev,
        ...incoming,
        attachments: Array.isArray(incoming.attachments)
          ? incoming.attachments
          : [],
        comments: mergedComments,
      };
      setMany(merged);
      if (commitAsBase) commitBase(merged);
    },
    [
      emptyDraft,
      setMany,
      commitBase,
      isEditing,
      dirty,
      applyIncomingCommentsToDraft,
    ]
  );

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    if (!selectedCompanyId) return;
    try {
      const cachedOrFresh = await fetchAnnouncementById?.(id, {
        preferCache: true,
        force: false,
      });
      if (cachedOrFresh) {
        applyIncomingDetail(cachedOrFresh, { commitAsBase: true });
      }
    } catch (e) {
      console.warn("Announcement cached fetch failed:", e);
    }
    const forceKey = `${selectedCompanyId}:${String(id)}`;
    if (didForceFetchRef.current.has(forceKey)) return;
    didForceFetchRef.current.add(forceKey);
    try {
      const fresh = await fetchAnnouncementById?.(id, {
        preferCache: true,
        force: true,
      });
      if (fresh) {
        applyIncomingDetail(fresh, { commitAsBase: true });
      }
    } catch (e) {
      console.warn("Announcement force fetch failed:", e);
    }
  }, [id, selectedCompanyId, fetchAnnouncementById, applyIncomingDetail]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const markAsViewedOnce = useCallback(() => {
    if (!id) return;
    if (!selectedCompanyId) return;
    const uid = user?.uid;
    if (!uid) return;
    const viewedKey = `${selectedCompanyId}:${String(id)}:${String(uid)}`;
    if (didMarkViewedRef.current.has(viewedKey)) return;
    didMarkViewedRef.current.add(viewedKey);
    Promise.resolve()
      .then(() => markViewed?.(id, uid, selectedCompanyId))
      .catch(() => {});
  }, [id, selectedCompanyId, user?.uid, markViewed]);

  useEffect(() => {
    markAsViewedOnce();
  }, [markAsViewedOnce]);

  const setDraftField = useCallback(
    (key, value) => setField(key, value),
    [setField]
  );

  const saveAnnouncement = useCallback(async () => {
    if (!selectedCompanyId) return;
    if (id && !dirty) {
      setIsEditing(false);
      return;
    }
    const payload = {
      title: (draftRef.current?.title || "").trim(),
      content: (draftRef.current?.content || "").trim(),
      category: draftRef.current?.category || "",
      audience: draftRef.current?.audience || "residents_owners",
    };
    if (!payload.title || !payload.content) return;
    try {
      if (!id) {
        const res = await createAnnouncement(selectedCompanyId, {
          ...payload,
          createdBy: user?.uid || null,
        });
        showToast("Tiedote luotu", "success", 1800);
        navigate(`/announcementnew2/${res.id}`, { replace: true });
        return;
      }
      await updateAnnouncement(selectedCompanyId, id, payload);
      const now = Date.now();
      const merged = { ...draftRef.current, ...payload, updatedAt: now };
      setMany(merged);
      commitBase(merged);
      setIsEditing(false);
      showToast("Tiedote tallennettu", "success", 1800);
    } catch (e) {
      console.error("Announcement save failed:", e);
      showToast("Tallennus epäonnistui", "error", 2500);
    }
  }, [
    id,
    dirty,
    selectedCompanyId,
    user?.uid,
    createAnnouncement,
    updateAnnouncement,
    navigate,
    showToast,
    setMany,
    commitBase,
  ]);

  const shouldShowLoadingPlaceholder = useMemo(() => {
    if (!id) return false;
    const hasSomething =
      Boolean(safeDraft?.title) ||
      Boolean(safeDraft?.content) ||
      (safeDraft?.attachments || []).length > 0 ||
      (safeDraft?.comments || []).length > 0;
    return !hasSomething && (detailLoading || !selectedCompanyId);
  }, [id, safeDraft, detailLoading, selectedCompanyId]);

  const renderMainContent = () => {
    if (!isEditing) {
      return (
        <AnnouncementDetailViewMode
          safeDraft={safeDraft}
          isCompactView={isCompactView}
        />
      );
    }
    return (
      <AnnouncementDetailEditMode
        safeDraft={safeDraft}
        isBusy={isBusy}
        announcementId={announcementId}
        setDraftField={setDraftField}
        handleUploadFiles={handleUploadFiles}
        handleDeleteAttachment={handleDeleteAttachment}
      />
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div
          className={`${styles.headerRow} ${
            isCompactView ? styles.headerRowCompact : ""
          }`}
        >
          {!isCompactView && (
            <>
              <div className={styles.headerLeft}>
                <div className={styles.titleIcon}>
                  <IconAnnouncements />
                </div>
                <h1 className={styles.h1}>Tiedotteet</h1>
              </div>
              <div className={styles.mlAuto} />
              <div className={styles.backLine}>
                <button
                  className={styles.backLink}
                  type="button"
                  onClick={() => navigate("/announcements")}
                >
                  ← Takaisin tiedotteisiin
                </button>
              </div>
            </>
          )}
        </div>
        <div
          className={`${styles.userlistCard} ${
            isCompactView ? styles.userlistCardCompact : ""
          }`}
        >
          {shouldShowLoadingPlaceholder ? (
            <div className={styles.mutedSmall} style={{ padding: 14 }}>
              Haetaan tiedotetta…
            </div>
          ) : (
            <>
              {!isCompactView ? (
                <div className={styles.cardHeaderRow}>
                  <div className={styles.cardHeaderLeft}>
                    {!isEditing ? (
                      <div className={styles.titleBlock}>
                        <h2 className={styles.topTitle}>
                          {safeDraft.title || (id ? "Tiedote" : "Uusi tiedote")}
                        </h2>
                        {safeDraft.category && (
                          <span className={styles.pill}>
                            {safeDraft.category}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className={styles.metaRow}>
                        <div className={styles.metaItem}>
                          <div className={styles.metaLabel}>Luotu</div>
                          <div className={styles.metaValue}>
                            {fmtDateTime(safeDraft.createdAt)}
                          </div>
                        </div>
                        <div className={styles.metaItem}>
                          <div className={styles.metaLabel}>Päivitetty</div>
                          <div className={styles.metaValue}>
                            {fmtDateTime(safeDraft.updatedAt)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.topActions}>
                    {!isEditing && isManager && id && (
                      <button
                        className={styles.primaryBtn}
                        onClick={() => setIsEditing(true)}
                      >
                        Muokkaa
                      </button>
                    )}
                    {isEditing && (
                      <button
                        className={styles.primaryBtn}
                        onClick={saveAnnouncement}
                        disabled={
                          isBusy ||
                          !safeDraft.title.trim() ||
                          !safeDraft.content.trim() ||
                          (id ? !dirty : false)
                        }
                        title={
                          id && !dirty ? "Ei muutoksia tallennettavaksi" : ""
                        }
                      >
                        {saving
                          ? "Tallennetaan…"
                          : id
                          ? "Tallenna"
                          : "Luo tiedote"}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className={styles.mobileTitleHeader}>
                  {!isEditing ? (
                    <>
                      <div className={styles.mobileMetaTopRow}>
                        {safeDraft.category ? (
                          <span className={styles.pill}>
                            {safeDraft.category}
                          </span>
                        ) : (
                          <span />
                        )}
                        <button
                          className={styles.backLink}
                          type="button"
                          onClick={() => navigate("/announcements")}
                        >
                          ← Takaisin tiedotteisiin
                        </button>
                      </div>
                      <h2 className={styles.topTitle}>
                        {safeDraft.title || (id ? "Tiedote" : "Uusi tiedote")}
                      </h2>
                    </>
                  ) : (
                    <>
                      <div className={styles.mobileMetaTopRow}>
                        <span />
                        <button
                          className={styles.backLink}
                          type="button"
                          onClick={() => navigate("/announcements")}
                        >
                          ← Takaisin tiedotteisiin
                        </button>
                      </div>
                      <div className={styles.topActions}>
                        <button
                          className={styles.primaryBtn}
                          onClick={saveAnnouncement}
                          disabled={
                            isBusy ||
                            !safeDraft.title.trim() ||
                            !safeDraft.content.trim() ||
                            (id ? !dirty : false)
                          }
                          title={
                            id && !dirty ? "Ei muutoksia tallennettavaksi" : ""
                          }
                        >
                          {saving
                            ? "Tallennetaan…"
                            : id
                            ? "Tallenna"
                            : "Luo tiedote"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              {!isCompactView ? (
                <div className={styles.detailGrid}>
                  <div className={styles.leftCol}>{renderMainContent()}</div>
                  <div className={styles.rightCol}>
                    <CommentsSection
                      user={user}
                      comments={safeDraft.comments || []}
                      onSend={handleSendComment}
                      onReplySubmit={handleReplySubmit}
                      onLike={handleLike}
                      loading={detailLoading}
                      canPost={Boolean(user)}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.compactBody}>
                  <div className={`${styles.leftCol} ${styles.leftColCompact}`}>
                    {renderMainContent()}
                    <div className={styles.compactCommentsWrap}>
                      <CommentsSection
                        user={user}
                        comments={safeDraft.comments || []}
                        onSend={handleSendComment}
                        onReplySubmit={handleReplySubmit}
                        onLike={handleLike}
                        loading={detailLoading}
                        canPost={Boolean(user)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}